import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '../stores/gameStore';
import { useAuthStore } from '../stores/authStore';
import { ipChangeApi } from '../services/api';
import { getIpChangeCost, isIpChangePending, ipChangeMsRemaining } from '../types';

interface IpChangePanelProps {
  compact?: boolean; // true = inline button next to IP; false = card with full info
}

export default function IpChangePanel({ compact = false }: IpChangePanelProps) {
  const { player, loadPlayer } = useGameStore();
  const { userId } = useAuthStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msLeft, setMsLeft] = useState(0);

  // Countdown ticker
  useEffect(() => {
    if (!player?.ipChangePendingAt) { setMsLeft(0); return; }
    const update = () => setMsLeft(ipChangeMsRemaining(player.ipChangePendingAt));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [player?.ipChangePendingAt]);

  // Auto-reload only when the 20-minute window has truly elapsed.
  // DO NOT rely on msLeft === 0: msLeft starts at 0 before the ticker fires,
  // which would cause a spurious loadPlayer() on every mount and create an
  // infinite mount/unmount cycle when PlayerStats early-returns during loading.
  useEffect(() => {
    if (!player?.ipChangePendingAt) return;
    if (msLeft > 0) return;
    const elapsed = Date.now() - new Date(player.ipChangePendingAt).getTime();
    if (elapsed >= 20 * 60 * 1000) {
      loadPlayer();
    }
  }, [msLeft, player?.ipChangePendingAt, loadPlayer]);

  const handleInitiate = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      await ipChangeApi.initiate(userId);
      await loadPlayer();
    } catch (err: unknown) {
      const e = err as { response?: { data?: string | { message?: string } } };
      const msg = typeof e?.response?.data === 'string'
        ? e.response.data
        : (e?.response?.data as { message?: string })?.message ?? 'Failed to initiate IP change';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, [userId, loadPlayer]);

  if (!player) return null;

  const pending   = isIpChangePending(player.ipChangePendingAt);
  const cost      = getIpChangeCost(player.ipChangeCount ?? 0);
  const canAfford = player.credits >= cost;

  // 24-hour cooldown (only relevant when not pending)
  const lastChange = player.lastIpChangeAt ? new Date(player.lastIpChangeAt).getTime() : 0;
  const cooldownMs = 24 * 60 * 60 * 1000;
  const onCooldown = !pending && lastChange > 0 && (Date.now() - lastChange) < cooldownMs;
  const cooldownLeft = onCooldown ? cooldownMs - (Date.now() - lastChange) : 0;
  const cooldownHrs  = Math.floor(cooldownLeft / 3_600_000);
  const cooldownMins = Math.floor((cooldownLeft % 3_600_000) / 60_000);

  const fmtCountdown = (ms: number) => {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (compact) {
    // ── Inline variant (used inside PlayerStats) ──────────────────────────────
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        {pending ? (
          <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#fbbf24' }}>
            IP change: {fmtCountdown(msLeft)}
          </span>
        ) : onCooldown ? (
          <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#555' }}>
            Cooldown: {cooldownHrs}h {cooldownMins}m
          </span>
        ) : (
          <button
            onClick={handleInitiate}
            disabled={busy || !canAfford}
            title={cost === 0 ? 'First change is free' : `Cost: ${cost.toLocaleString()} cr`}
            style={{
              background: 'transparent',
              border: '1px solid var(--primary-color)',
              color: canAfford ? 'var(--primary-color)' : '#555',
              padding: '0.1rem 0.45rem',
              borderRadius: '3px',
              cursor: canAfford && !busy ? 'pointer' : 'not-allowed',
              fontFamily: 'monospace',
              fontSize: '0.7rem',
            }}
          >
            {busy ? '...' : cost === 0 ? 'Change IP (free)' : `Change IP (${cost.toLocaleString()} cr)`}
          </button>
        )}
        {error && (
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#f87171' }}>{error}</span>
        )}
      </div>
    );
  }

  // ── Full card variant (used in Profile) ──────────────────────────────────────
  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="card-header">
        <h2 className="card-title">IP Address</h2>
      </div>

      <div style={{ fontFamily: 'monospace', display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Current IP</span>
          <span style={{ color: '#4ade80' }}>{player.ipAddress}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Changes performed</span>
          <span>{player.ipChangeCount ?? 0}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Next change cost</span>
          <span style={{ color: cost === 0 ? '#4ade80' : canAfford ? '#e2e8f0' : '#f87171' }}>
            {cost === 0 ? 'Free' : `${cost.toLocaleString()} cr`}
          </span>
        </div>

        {pending && (
          <div style={{ background: '#1a1600', border: '1px solid #fbbf24', borderRadius: '4px', padding: '0.75rem 1rem' }}>
            <div style={{ color: '#fbbf24', fontSize: '0.85rem', marginBottom: '0.25rem' }}>IP change in progress</div>
            <div style={{ color: '#888', fontSize: '0.8rem' }}>
              New IP: <span style={{ color: '#4ade80' }}>{player.ipChangePendingNewIp}</span>
            </div>
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.2rem' }}>
              Completing in <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>{fmtCountdown(msLeft)}</span>
            </div>
            <div style={{ color: '#555', fontSize: '0.75rem', marginTop: '0.4rem' }}>
              All game actions are blocked while the change is in progress.
            </div>
          </div>
        )}

        {onCooldown && !pending && (
          <div style={{ color: '#555', fontSize: '0.8rem' }}>
            Next change available in {cooldownHrs}h {cooldownMins}m
          </div>
        )}

        {error && (
          <div style={{ color: '#f87171', fontSize: '0.82rem' }}>{error}</div>
        )}

        {!pending && (
          <button
            onClick={handleInitiate}
            disabled={busy || !canAfford || onCooldown}
            style={{
              background: !canAfford || onCooldown ? '#1a1a2e' : 'var(--primary-color)',
              color: !canAfford || onCooldown ? '#555' : '#000',
              border: !canAfford || onCooldown ? '1px solid #333' : 'none',
              padding: '0.55rem 1.25rem',
              borderRadius: '4px',
              cursor: !busy && canAfford && !onCooldown ? 'pointer' : 'not-allowed',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              width: '100%',
            }}
          >
            {busy ? 'Initiating...' : onCooldown ? 'Cooldown active' : cost === 0 ? 'Change IP Address (free)' : `Change IP Address — ${cost.toLocaleString()} cr`}
          </button>
        )}

        <p style={{ color: '#555', fontSize: '0.75rem', lineHeight: '1.5', margin: 0 }}>
          Changing your IP makes you untraceable to players who previously hacked you.
          The process takes 20 minutes; all game actions are blocked during this time.
          You can only change your IP once every 24 hours. The cost increases with each change.
        </p>
      </div>
    </div>
  );
}
